import { supabase } from "@/lib/supabaseClient";

/**
 * Checks if a string is a simple bare media name (e.g. 'media_1779249262771_9dw7akq').
 */
export function isBareMediaName(value: string | undefined | null): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  
  if (
    trimmed.startsWith("http://") || 
    trimmed.startsWith("https://") || 
    trimmed.startsWith("blob:") || 
    trimmed.startsWith("data:") ||
    trimmed.includes("/") ||
    trimmed.includes("\\")
  ) {
    return false;
  }
  
  return trimmed.startsWith("media_");
}

/**
 * Searches the Supabase Storage buckets ('question-audios', 'question-images') for
 * a physical file matching the bare mediaName, under common folders and user ID prefixes.
 * Returns the resolved public URL, or a candidate root URL as a fallback.
 */
export async function resolveBareMediaNameToPublicUrl(
  mediaName: string, 
  type: "audio" | "image",
  strict: boolean = false
): Promise<string | null> {
  if (!mediaName) return null;
  const trimmed = mediaName.trim();
  if (!trimmed) return null;

  if (!supabase) {
    console.warn("[CO_MEDIA_REPAIR_RESOLVER] Supabase client is not configured.");
    return null;
  }

  const buckets = type === "audio" 
    ? ["question-audios", "question-images"] 
    : ["question-images", "question-audios"];

  // 1. Gather candidate folders, including the current authenticated user's ID
  const commonFolders = [""];
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (userId) {
      commonFolders.push(userId);
    }
  } catch (err) {
    console.warn("[CO_MEDIA_REPAIR_RESOLVER] User ID retrieval failed:", err);
  }

  commonFolders.push("audio", "audios", "images", "question-media", "public");

  console.log("[CO_MEDIA_REPAIR_DETECTED_BARE_NAMES]", {
    mediaName: trimmed,
    type,
    searchFolders: commonFolders,
    searchBuckets: buckets
  });

  // 2. Scan both buckets and folders to check if the file physically exists
  for (const bucket of buckets) {
    for (const folder of commonFolders) {
      try {
        const { data: files, error } = await supabase.storage.from(bucket).list(folder, {
          limit: 100
        });

        if (error) {
          // Silent proceed for missing directories or permissions
          continue;
        }

        if (files && files.length > 0) {
          // Find any file that contains the bare mediaName in its filename
          const matchedFile = files.find(f => {
            const nameWithoutExt = f.name.split(".")[0];
            return f.name === trimmed || nameWithoutExt === trimmed || f.name.includes(trimmed);
          });

          if (matchedFile) {
            const path = folder ? `${folder}/${matchedFile.name}` : matchedFile.name;
            const { data } = supabase.storage.from(bucket).getPublicUrl(path);
            
            if (data?.publicUrl) {
              console.log("[CO_MEDIA_REPAIR_RESULT]", {
                mediaName: trimmed,
                bucket,
                resolvedPath: path,
                publicUrl: data.publicUrl,
                success: true
              });
              return data.publicUrl;
            }
          }
        }
      } catch (err) {
        console.warn(`[CO_MEDIA_REPAIR_RESOLVER] Failed to list ${folder} in bucket ${bucket}:`, err);
      }
    }
  }

  // 3. Fallback: generate default candidate URL in the primary bucket at root if not strict
  if (strict) {
    console.log("[CO_MEDIA_REPAIR_CANDIDATE_URLS_STRICT_FAILED]", {
      mediaName: trimmed,
      type
    });
    return null;
  }

  const primaryBucket = buckets[0];
  const { data } = supabase.storage.from(primaryBucket).getPublicUrl(trimmed);
  const candidateUrl = data?.publicUrl || null;

  console.log("[CO_MEDIA_REPAIR_CANDIDATE_URLS]", {
    mediaName: trimmed,
    primaryBucket,
    candidateUrl
  });

  return candidateUrl;
}
